import PropTypes from 'prop-types';

import useTypewriter from './useTypewriter';

// Left panel of the login split-screen: a radial gradient surface with an
// accent glow and the Z blob as a masked watermark. Types out the welcome
// message as the panel tagline and renders optional children, e.g. the
// enrollment stepper. The brand color comes from the --zippedi-accent token.
const STYLES = {
  sidebar: {
    position: 'relative',
    // Wider than the current sidebar (1.1 vs 0.8 against the content's flex:1),
    // so this variant's left panel takes ~52% of the viewport instead of ~44%.
    flex: '1.1 1 0',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '44px 38px',
    // TODO: .login-shell hardcodes a different gradient for this same backdrop.
    // Decide whether they should match.
    background:
      'radial-gradient(120% 140% at 20% 10%, #2a1f2c 0%, #1a1420 45%, #12101a 100%)',
    borderRadius: '0 24px 24px 0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  glow: {
    position: 'absolute',
    inset: 0,
    // Thins the token's 0.35 alpha to an effective 0.165 over the gradient.
    opacity: 0.47,
    background:
      'radial-gradient(60% 50% at 75% 80%, var(--zippedi-accent-glow) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  // Faint white blob behind the accent one, rotated for depth.
  blobBack: {
    position: 'absolute',
    bottom: '-100px',
    right: '-100px',
    width: '420px',
    height: '479px',
    opacity: 0.06,
    background: '#ffffff',
    WebkitMaskImage: "url('/images/z-blob.png')",
    maskImage: "url('/images/z-blob.png')",
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    transform: 'rotate(-8deg) scale(1.3)',
    pointerEvents: 'none',
  },
  // Accent-tinted blob on top.
  blobFront: {
    position: 'absolute',
    bottom: '-10px',
    right: '-40px',
    width: '340px',
    height: '388px',
    opacity: 0.12,
    background: 'var(--zippedi-accent)',
    WebkitMaskImage: "url('/images/z-blob.png')",
    maskImage: "url('/images/z-blob.png')",
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    pointerEvents: 'none',
  },
  // Wordmark + tagline as one block, pushed down from the top edge.
  brandBlock: {
    marginTop: '48px',
  },
  brandWordmark: {
    position: 'relative',
    zIndex: 1,
    fontSize: '22px',
    fontWeight: 800,
    letterSpacing: '0.18em',
    color: 'var(--zippedi-text-on-dark-primary)',
  },
  content: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  // Sits directly under the wordmark, typed out on mount. Larger than the
  // form's subtitle so it reads as the panel's headline.
  tagline: {
    position: 'relative',
    zIndex: 1,
    marginTop: '32px',
    fontSize: '22px',
    fontWeight: 400,
    lineHeight: 1.35,
    color: 'var(--zippedi-text-on-dark-primary)',
    opacity: 0.85,
    maxWidth: '420px',
    // Reserve two lines so the layout does not jump as it types. The sr-only
    // copy of the message cannot do this: it is absolutely positioned, so it
    // carries no height.
    minHeight: '2.7em',
  },
  // Blinking caret shown while the tagline types; hidden once it finishes.
  // color: currentColor drives the animate-caret-blink keyframe, which
  // animates background-color.
  caret: {
    display: 'inline-block',
    width: '2px',
    height: '1.1em',
    marginLeft: '2px',
    verticalAlign: 'text-bottom',
    color: 'var(--zippedi-text-on-dark-primary)',
    backgroundColor: 'currentColor',
  },
};

export default function LoginSidebar({ message, children }) {
  // The tagline is the per-app welcome message resolved by heimdal-api, typed
  // out on mount. It only renders when the backend returns one: an unregistered
  // app or an empty default leaves the sidebar with just the wordmark.
  const { typed, done } = useTypewriter(message || '');

  return (
    <div style={STYLES.sidebar}>
      <div style={STYLES.glow} />
      <div style={STYLES.blobBack} />
      <div style={STYLES.blobFront} />

      <div style={STYLES.brandBlock}>
        <span style={STYLES.brandWordmark}>ZIPPEDI</span>
        {/* role=paragraph is name-prohibited in ARIA, so aria-label would be
            ignored here: the full text goes in a visually hidden node instead.
            The typed span is aria-hidden to avoid partial reads. */}
        {message ? (
          <p style={STYLES.tagline}>
            <span className="sr-only">{message}</span>
            <span aria-hidden="true">{typed}</span>
            {!done ? (
              <span
                className="animate-caret-blink"
                style={STYLES.caret}
                aria-hidden="true"
              />
            ) : null}
          </p>
        ) : null}
      </div>

      {children ? <div style={STYLES.content}>{children}</div> : null}
    </div>
  );
}

LoginSidebar.propTypes = {
  message: PropTypes.string,
  children: PropTypes.node,
};
